import { useCallback, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import tripPlannerGlossaryMessages from 'tripPlanner/i18n/tripPlannerGlossaryMessages'
import AssistChip from 'LuxKit/Chips/AssistChip'
import TextButton from 'LuxKit/Button/TextButton'
import LinePlusIcon from 'LuxKit/Icons/line/LinePlusIcon'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import AddToTripDropdown from './AddToTripDropdown'
import type { TripItineraryData } from 'tripPlanner/api/types'
import { isPlaceInItinerary, getItineraryDayLabel, findPlaceStopInItinerary, findOfferStopDetails, findNearestStop, type Coordinates } from 'tripPlanner/utils/chatItinerary'
import { getLocalisedDayLabel } from 'tripPlanner/utils/chatDates'
import type { SendMessageOptions } from 'tripPlanner/components/AddToTrip/useAddHotelToTrip'
import { useChatStreamStatus } from 'tripPlanner/pages/chat/ChatTripPlannerPage/contexts/ChatStreamStatusContext'

interface Props {
  itineraryData: TripItineraryData
  onSendMessage: (message: string, options?: SendMessageOptions) => void
  placeName: string
  placeId?: string
  offerId?: string
  offerType?: string
  coordinates?: Coordinates
  variant?: 'filled' | 'outline' | 'button'
  buttonKind?: 'primary' | 'secondary'
  onAfterAction?: () => void
}

function AddToTripAction({
  itineraryData,
  onSendMessage,
  placeName,
  placeId,
  offerId,
  offerType,
  coordinates,
  variant = 'outline',
  buttonKind = 'primary',
  onAfterAction,
}: Props) {
  const intl = useIntl()
  const addToTripLabel = intl.formatMessage(tripPlannerGlossaryMessages.addToTrip)

  const [isOpen, setIsOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)

  const offerStopDetails = useMemo(
    () => findOfferStopDetails(offerId, itineraryData),
    [offerId, itineraryData],
  )

  const placeInItinerary = useMemo(
    () => !!offerStopDetails || isPlaceInItinerary(placeId, itineraryData),
    [offerStopDetails, placeId, itineraryData],
  )

  const itineraryDayLabel = useMemo(() => {
    if (offerStopDetails) {
      return offerStopDetails.dayNumber !== undefined
        ? getLocalisedDayLabel(intl, offerStopDetails.dayNumber, offerStopDetails.dayDate)
        : intl.formatMessage({
            id: 'MgK5ISSCJKWklR8eg12e',
            defaultMessage: 'To be planned',
            description: 'Label for unassigned trip itinerary items that have no scheduled day',
          })
    }
    return getItineraryDayLabel(placeId, itineraryData)
  }, [offerStopDetails, placeId, itineraryData, intl])

  const placeStopInfo = useMemo(
    () => findPlaceStopInItinerary(placeId, itineraryData),
    [placeId, itineraryData],
  )

  const nearestStop = useMemo(
    () => findNearestStop(coordinates, itineraryData?.stops ?? []),
    [coordinates, itineraryData?.stops],
  )

  const initialStop = offerStopDetails?.stop ?? placeStopInfo?.stop ?? nearestStop

  const messageOptions = useMemo<SendMessageOptions | undefined>(
    () => offerId ? { offerId, offerType } : undefined,
    [offerId, offerType],
  )

  const { isStreaming } = useChatStreamStatus()

  const toggleDropdown = useCallback(() => {
    setIsOpen(prev => !prev)
  }, [])

  const closeDropdown = useCallback(() => {
    setIsOpen(false)
  }, [])

  const label = placeInItinerary ? itineraryDayLabel : addToTripLabel
  const triggerProps = {
    ref: triggerRef,
    onClick: toggleDropdown,
    startIcon: placeInItinerary ? undefined : <LinePlusIcon />,
    endIcon: placeInItinerary ? <LineAngleDownIcon /> : undefined,
    disabled: isStreaming,
  }

  const trigger = variant === 'button'
    ? <TextButton kind={buttonKind} size="large" {...triggerProps}>{label}</TextButton>
    : <AssistChip variant={variant} size="small" {...triggerProps}>{label}</AssistChip>

  return (
    <>
      {trigger}
      <AddToTripDropdown
        open={isOpen}
        onClose={closeDropdown}
        triggerRef={triggerRef}
        anchorRef={triggerRef}
        itineraryData={itineraryData}
        placeName={placeName}
        onSendMessage={onSendMessage}
        sendMessageOptions={messageOptions}
        onAfterAction={onAfterAction}
        initialStop={initialStop}
        currentDayNumber={offerStopDetails?.dayNumber ?? placeStopInfo?.dayNumber}
      />
    </>
  )
}

export default AddToTripAction
